<script setup lang="ts">
import { ref, provide, watch, onMounted, onUnmounted } from 'vue';
import useMaskStore from '@shop/store/mask';

const selectedKey = ref<string | null>(null);
const overflowing = ref<boolean>(false);
const containerRef = ref<HTMLElement | null>(null);
const maskStore = useMaskStore();

const checkOverflow = () => {
  const el = containerRef.value;
  if (!el || el.clientWidth > 992) {
    overflowing.value = false;
    return;
  }
  const items = el.querySelectorAll<HTMLElement>('.main-menu__head-item');
  let totalWidth = 0;
  items.forEach(item => {
    totalWidth += item.clientWidth;
  });
  overflowing.value = totalWidth > el.clientWidth;
};

const closeMenu = () => {
  selectedKey.value = null;
  maskStore.close();
};

const openMenu = (key: string) => {
  selectedKey.value = key;
  maskStore.show();
};

const toggle = (key?: string | Event) => {
  if (typeof key === 'string') {
    if (selectedKey.value === key) {
      closeMenu();
    } else {
      openMenu(key);
    }
    return;
  }

  if (key && key instanceof Event && key.target) {
    const target = key.target as HTMLElement;
    const link = target.closest('a[data-item-key]') as HTMLElement | null;
    const itemKey = link?.getAttribute('data-item-key');

    if (itemKey) {
      if (selectedKey.value === itemKey) {
        closeMenu();
      } else {
        openMenu(itemKey);
      }
    }
  }
};

provide('mainMenu', {
  selectedKey,
  overflowing,
  toggle,
  openMenu,
  closeMenu,
});

watch(
  () => maskStore.visible,
  visible => {
    if (!visible && selectedKey.value) {
      selectedKey.value = null;
    }
  }
);

onMounted(() => {
  checkOverflow();
  window.addEventListener('resize', checkOverflow);
});

onUnmounted(() => {
  window.removeEventListener('resize', checkOverflow);
});

defineExpose({ selectedKey, overflowing, toggle, openMenu, closeMenu, checkOverflow });
</script>

<template>
  <div
    ref="containerRef"
    class="main-menu__container"
    :class="{
      'main-menu__container--open force-zindex--high': selectedKey,
      'main-menu__container--overflowing': overflowing,
    }"
    @mouseleave="closeMenu"
  >
    <slot
      :selected-key="selectedKey"
      :overflowing="overflowing"
      :toggle="toggle"
      :close-menu="closeMenu"
    >
    </slot>
  </div>
</template>

<style lang="scss"></style>
